import { RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { usePermission } from '../../../../auth/AuthContext';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { AwardModal } from '../components/AwardModal';
import { awardCategoriesApi, nominationsApi } from '../praiseApi';
import type { PraiseSurface } from '../praiseSurface';
import { AWARD_AUDIT_ENTITY_TYPES, type AwardListItem } from '../praiseTypes';

/**
 * PRAISE › Nomination and PRAISE › Evaluation — every award opened for nomination. ONE page for both
 * surfaces, handed the surface's endpoints: Nomination opens, edits and deletes awards; Evaluation
 * reads them and draws no write control at all.
 *
 * **The folder nav is the CATEGORY** — a CATALOG facet, every live category with its zero, so the
 * strip reads the six award kinds whether or not anything has been opened under one.
 */
export function AwardsIndexPage({ surface }: { surface: PraiseSurface }) {
  const navigate = useNavigate();
  const toast = useToast();
  const writes = surface.writes;

  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<{ kind: 'delete' | 'restore'; row: AwardListItem } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  /*
    WHAT AN AWARD CANNOT BE OPENED WITHOUT — a category whose checklist holds a document. Seeded TRUE,
    and a failed feed (or a reader without the catalog key) leaves it true: a request that did not
    come back is not evidence that the list is empty. Nomination only — Evaluation creates nothing.
  */
  const canCreate = usePermission('award-nominations.create');
  const [hasChecklist, setHasChecklist] = useState(true);

  useEffect(() => {
    if (!writes || !canCreate) return;

    const controller = new AbortController();

    awardCategoriesApi
      .options(controller.signal)
      .then((rows) => {
        if (!controller.signal.aborted) setHasChecklist(rows.some((c) => c.documents.length > 0));
      })
      .catch(() => undefined);

    return () => controller.abort();
  }, [writes, canCreate, reloadKey]);

  const fetchRows = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      surface.api.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
          category: query.facets.category?.join(','),
        },
        signal,
      ),
    [surface],
  );

  const rowActions = useCallback(
    (row: AwardListItem): MenuItem[] =>
      writes
        ? [
            {
              key: `restore-${row.id}`,
              label: 'Restore',
              icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
              permission: 'award-nominations.delete',
              disabledReason: row.isActive ? 'This award is already active.' : null,
              onClick: () => setConfirming({ kind: 'restore', row }),
            },
          ]
        : [],
    [writes],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'delete') await nominationsApi.remove(confirming.row.id);
      else await nominationsApi.restore(confirming.row.id);

      toast.success(`${confirming.row.categoryName} was ${confirming.kind === 'delete' ? 'deleted' : 'restored'}.`);
      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<AwardListItem>
        module={surface.module}
        breadcrumb={['Human Resources', 'PRAISE', surface.label]}
        columns={COLUMNS}
        facets={[
          { key: 'status', label: 'Status' },
          { key: 'category', label: 'Category' },
        ]}
        folderNavFacet="category"
        auditEntityTypes={AWARD_AUDIT_ENTITY_TYPES}
        rowKey={(row) => row.id}
        fetch={fetchRows}
        reloadKey={reloadKey}
        searchPlaceholder="Search by award or reference number"
        emptyMessage={
          writes
            ? 'No award opened for nomination yet.'
            : 'No award has been opened for nomination yet. Awards are opened under PRAISE → Nomination.'
        }
        prerequisites={
          writes
            ? [
                {
                  // Read into "Set up … first" / "Nothing has been added to … yet" / "Go to …".
                  label: 'an award category checklist',
                  met: hasChecklist,
                  to: '/praise/settings/award-categories',
                  permission: 'award-categories.read',
                },
              ]
            : undefined
        }
        onCreate={writes ? () => setCreating(true) : undefined}
        createPermission="award-nominations.create"
        onRowOpen={(row) => navigate(`${surface.basePath}/${row.id}`)}
        openPermission={surface.readPermission}
        rowActions={rowActions}
        onDelete={writes ? (row) => setConfirming({ kind: 'delete', row }) : undefined}
        deletePermission="award-nominations.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(row) => (row.isActive ? null : 'This award is already deleted.')}
        moduleNoun="awards"
        otherInfo={(row) => row}
      />

      {creating ? (
        <AwardModal
          open
          section="create"
          editing={null}
          onClose={() => setCreating(false)}
          onSaved={(saved, options) => {
            // Add another keeps the form up and the list fresh; otherwise go to the new award.
            if (options?.keepOpen) {
              refresh();
              return;
            }
            setCreating(false);
            navigate(`${surface.basePath}/${saved.id}`);
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'delete' ? 'Delete award' : 'Restore award'}
          danger={confirming.kind === 'delete'}
          requireConfirm={confirming.kind === 'delete'}
          requireCredential={confirming.kind === 'delete'}
          loading={running}
          id={`award-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              {confirming.kind === 'delete' ? (
                <>
                  <p>{confirming.row.categoryName} is hidden from Nomination and Evaluation.</p>
                  <p>Nothing is erased — its nominees and their documents stay, and a deleted award can be restored.</p>
                </>
              ) : (
                <p>{confirming.row.categoryName} returns where it was, with its nominees.</p>
              )}

              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<AwardListItem>[] = [
  // The CATEGORY names the award — there is no title of its own (03 Nov 2026). Ties read apart by the reference.
  {
    key: 'categoryName',
    header: 'Award',
    width: 300,
    flex: true,
    sortable: true,
    render: (row) => <TextCell value={row.categoryName} />,
  },
  {
    key: 'referenceNumber',
    header: 'Reference',
    width: 160,
    sortable: true,
    summary: true,
    render: (row) => <TextCell value={row.referenceNumber} mono />,
  },
  {
    key: 'nomineeCount',
    header: 'Nominees',
    width: 110,
    align: 'right',
    summary: true,
    render: (row) => <TextCell value={String(row.nomineeCount)} mono numeric />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (row) => (row.isActive ? <StatusText tone="active">Active</StatusText> : <StatusText tone="inactive">Deleted</StatusText>),
  },
];
